import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { vorApi } from '../services/api';
import { toast } from 'react-toastify';

const fmtCurrency = (val) =>
  val != null ? `$${Number(val).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—';

const fmtPct = (val) =>
  val != null ? `${Number(val).toFixed(1)}%` : '—';

const Section = ({ title, children }) => (
  <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px', overflow: 'hidden' }}>
    <div style={{ padding: '14px 20px', backgroundColor: '#F3EEFA', borderBottom: '1px solid #E5D5F5' }}>
      <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#4A2B70' }}>{title}</h3>
    </div>
    <div style={{ padding: '20px' }}>{children}</div>
  </div>
);

const Field = ({ label, value }) => (
  <div style={{ marginBottom: '12px' }}>
    <span style={{ fontSize: '12px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
    <div style={{ fontSize: '15px', color: '#111827', marginTop: '2px', fontWeight: '500' }}>{value || '—'}</div>
  </div>
);

const STEPS = [
  'Basic Info', 'Risk Tolerance', 'Cost Inputs', 'Credible Risks', 'Controls', 'Results & Report'
];

const VorAssessmentDetail = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    vorApi.getAssessment(assessmentId)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load assessment'))
      .finally(() => setLoading(false));
  }, [assessmentId]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this assessment? This cannot be undone.')) return;
    try {
      await vorApi.deleteAssessment(assessmentId);
      toast.success('Assessment deleted');
      navigate('/vor/assessments');
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleDownload = async () => {
    try {
      await vorApi.downloadReport(assessmentId, data?.assessment?.client_name);
      toast.success('Report downloaded');
    } catch {
      toast.error('Report generation failed');
    }
  };

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: '#6B7280' }}>Loading…</div>;
  if (!data) return <div style={{ padding: '40px', color: '#DC2626' }}>Assessment not found.</div>;

  const { assessment, client, risk_tolerance, cost_inputs, credible_risks, controls, results, recommended_actions } = data;
  const hasResults = !!results?.baseline_risk_value;

  // Determine workflow completion per step
  const stepsDone = [
    true,                                                    // Step 1: always
    (risk_tolerance || []).length > 0,                       // Step 2
    !!cost_inputs?.estimated_outage_hours,                   // Step 3
    (credible_risks || []).length > 0,                       // Step 4
    (controls || []).length > 0,                             // Step 5
    hasResults,                                              // Step 6
  ];

  return (
    <div>
      {/* Breadcrumb + actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '6px' }}>
            <Link to="/vor/assessments" style={{ color: '#6B3FA0', textDecoration: 'none' }}>VoR Assessments</Link>
            {' / '}
            {assessment.assessment_name}
          </div>
          <h1 style={{ margin: 0, fontSize: '26px', fontWeight: '700', color: '#1a1a2e' }}>{assessment.assessment_name}</h1>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: '14px' }}>{client?.name}</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {hasResults && (
            <button onClick={handleDownload}
              style={{ padding: '9px 18px', backgroundColor: '#059669', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}>
              Download Report
            </button>
          )}
          <button onClick={() => navigate(`/vor/assessments/${assessmentId}/workflow`)}
            style={{ padding: '9px 18px', backgroundColor: '#6B3FA0', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}>
            {stepsDone.every(Boolean) ? 'Edit Workflow' : 'Continue Workflow'}
          </button>
          <button onClick={handleDelete}
            style={{ padding: '9px 18px', backgroundColor: 'white', color: '#DC2626', border: '1px solid #DC2626', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}>
            Delete
          </button>
        </div>
      </div>

      {/* Workflow progress */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '0', position: 'relative' }}>
          {STEPS.map((step, i) => {
            const done = stepsDone[i];
            return (
              <div key={step} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
                {i < STEPS.length - 1 && (
                  <div style={{ position: 'absolute', top: '16px', left: '50%', right: '-50%', height: '2px', backgroundColor: done && stepsDone[i + 1] ? '#6B3FA0' : '#E5D5F5', zIndex: 0 }} />
                )}
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%', margin: '0 auto 8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: done ? '#6B3FA0' : '#F3EEFA',
                  color: done ? 'white' : '#9CA3AF',
                  fontSize: '13px', fontWeight: '700', position: 'relative', zIndex: 1,
                }}>
                  {done ? '✓' : i + 1}
                </div>
                <div style={{ fontSize: '11px', color: done ? '#4A2B70' : '#9CA3AF', fontWeight: done ? '600' : 'normal' }}>
                  {step}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Results highlight */}
      {hasResults && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
          {[
            { label: 'Baseline Risk', value: fmtCurrency(results.baseline_risk_value), color: '#6B7280' },
            { label: 'Current Risk', value: fmtCurrency(results.total_current_risk_value), color: '#D97706' },
            { label: 'Future Risk', value: fmtCurrency(results.total_future_risk_value), color: '#059669' },
            { label: 'Risk Reduction', value: fmtCurrency(results.risk_reduction_value), color: '#6B3FA0' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: '700', color }}>{value}</div>
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Assessment info */}
        <Section title="Assessment Details">
          <Field label="Client" value={client?.name} />
          <Field label="Status" value={assessment.status?.replace('_', ' ')} />
          <Field label="Assessment Date" value={assessment.assessment_date} />
          <Field label="Risk Tolerance Level" value={results?.risk_tolerance_level} />
          <Field label="Created" value={new Date(assessment.created_at).toLocaleDateString()} />
        </Section>

        {/* Results breakdown */}
        {hasResults && (
          <Section title="Risk Breakdown">
            <Field label="Business Interruption" value={fmtCurrency(results.total_business_interruption_cost)} />
            <Field label="Financial Loss Exposure" value={fmtCurrency(results.total_financial_loss_exposure)} />
            <Field label="Recovery Costs" value={fmtCurrency(results.total_recovery_cost)} />
            <Field label="Compliance Risk" value={fmtCurrency(results.total_compliance_risk)} />
            <Field label="Reputation Risk" value={fmtCurrency(results.total_reputation_risk)} />
            <Field label="Current Control Reduction" value={fmtPct(results.current_control_reduction_percentage)} />
            <Field label="Recommended Control Reduction" value={fmtPct(results.recommended_control_reduction_percentage)} />
          </Section>
        )}

        {/* Credible risks */}
        {(credible_risks || []).length > 0 && (
          <Section title={`Credible Risks (${credible_risks.length})`}>
            {credible_risks.map((r, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: i < credible_risks.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                <div style={{ fontWeight: '500', fontSize: '14px', color: '#111827' }}>{r.risk_name}</div>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  {r.risk_category} · Score: {(r.current_likelihood || 0) * (r.current_impact || 0)}
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Controls */}
        {(controls || []).length > 0 && (
          <Section title={`Controls (${controls.length})`}>
            {controls.map((c, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: i < controls.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                <div style={{ fontWeight: '500', fontSize: '14px', color: '#111827' }}>{c.control_name}</div>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  {[c.is_current_control && 'Current', c.is_recommended_control && 'Recommended'].filter(Boolean).join(' · ')}
                </div>
              </div>
            ))}
          </Section>
        )}
      </div>

      {/* Recommended actions */}
      {(recommended_actions || []).length > 0 && (
        <Section title="Recommended Actions">
          {recommended_actions.map((a, i) => (
            <div key={i} style={{ padding: '10px 0', borderBottom: i < recommended_actions.length - 1 ? '1px solid #F3F4F6' : 'none', display: 'flex', gap: '12px' }}>
              <span style={{
                padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap',
                backgroundColor: a.priority === 'critical' ? '#FEE2E2' : a.priority === 'high' ? '#FEF3C7' : '#F3EEFA',
                color: a.priority === 'critical' ? '#991B1B' : a.priority === 'high' ? '#92400E' : '#4A2B70',
              }}>
                {(a.priority || 'medium').toUpperCase()}
              </span>
              <div>
                <div style={{ fontWeight: '500', fontSize: '14px' }}>{a.action_title}</div>
                {a.action_description && <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px' }}>{a.action_description}</div>}
              </div>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
};

export default VorAssessmentDetail;
